/**
 * Pure formatting/view-model helpers for the message-requests inbox
 * (`/messages/requests`). No I/O, no SDK imports, every input here is a
 * plain value already fetched by the screen that calls these.
 */

/**
 * A relative age for a request row's timestamp.
 *
 * Buckets, in order: under a minute is "Just now"; under an hour is minutes;
 * under a day is hours; under a week is days; anything older falls back to a
 * short absolute date ("Aug 3").
 */
export function formatRequestTimestamp(
  isoString: string,
  now: Date = new Date(),
): string {
  const deltaMs = now.getTime() - new Date(isoString).getTime();
  const deltaSec = Math.floor(deltaMs / 1000);

  if (deltaSec < 60) return "Just now";

  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) {
    return deltaMin === 1 ? "1 minute ago" : `${deltaMin} minutes ago`;
  }

  const deltaHour = Math.floor(deltaMin / 60);
  if (deltaHour < 24) {
    return deltaHour === 1 ? "1 hour ago" : `${deltaHour} hours ago`;
  }

  const deltaDay = Math.floor(deltaHour / 24);
  if (deltaDay < 7) {
    return deltaDay === 1 ? "1 day ago" : `${deltaDay} days ago`;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(isoString));
}

/** Clamps a preview to one line's worth of characters, ellipsis on overflow. */
export function truncateOneLine(text: string, maxChars = 120): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 1).trimEnd()}…`;
}

/** `Requests` with zero pending, `Requests · N` once there is at least one. */
export function resolveRequestsHeaderTitle(count: number): string {
  return count === 0 ? "Requests" : `Requests · ${count}`;
}

/**
 * The shared-pool context line for a row, or `null` when the sender shares no
 * pool with the viewer. Copy locked verbatim from
 * `.agents/design-prompts/51-message-requests-and-moderation.md`'s `## Copy`
 * section. Do not paraphrase.
 */
export function resolveSharedPoolContextLine(
  sharedPoolName: string | null,
): string | null {
  return sharedPoolName === null ? null : `In ${sharedPoolName} with you.`;
}
